import { useEffect, useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { 
  FaCheckCircle, 
  FaSpinner, 
  FaMapMarkerAlt, 
  FaCloudUploadAlt,
  FaDatabase,
  FaExclamationTriangle,
  FaLightbulb
} from 'react-icons/fa';
import { MdVerified } from 'react-icons/md';
import '../../styles/Processando.css';

// Função para obter geolocalização
const getGeolocation = () => {
  return new Promise((resolve, reject) => {
    if (!navigator.geolocation) {
      reject(new Error('Geolocalização não suportada pelo navegador'));
      return;
    }

    navigator.geolocation.getCurrentPosition(
      (position) => {
        const { latitude, longitude } = position.coords;
        resolve(`${latitude},${longitude}`);
      },
      (error) => {
        console.error('Erro ao obter localização:', error);
        reject(error);
      },
      {
        enableHighAccuracy: true,
        timeout: 10000,
        maximumAge: 0
      }
    );
  });
};

export default function ProcessandoScreen() {
  const navigate = useNavigate();
  const location = useLocation();
  const { apiService } = useAuth();
  
  // Pegar parâmetros da URL
  const searchParams = new URLSearchParams(location.search);
  const litros = searchParams.get('litros');
  const dados = searchParams.get('dados');
  const solicitacaoId = searchParams.get('solicitacaoId');
  
  const [status, setStatus] = useState('Iniciando processamento...');
  const [etapaAtual, setEtapaAtual] = useState(0);
  const [erro, setErro] = useState(null);
  const [progress, setProgress] = useState(0);
  const [solicitacao, setSolicitacao] = useState(null);

  // Etapas do processo
  const etapas = [
    { id: 1, nome: 'Validando dados', icon: <MdVerified />, status: 'pending' },
    { id: 2, nome: 'Obtendo localização', icon: <FaMapMarkerAlt />, status: 'pending' },
    { id: 3, nome: 'Enviando para o servidor', icon: <FaCloudUploadAlt />, status: 'pending' },
    { id: 4, nome: 'Confirmando registro', icon: <FaDatabase />, status: 'pending' }
  ];

  useEffect(() => {
    // dados da solicitação
    if (dados) {
      try {
        const parsed = JSON.parse(decodeURIComponent(dados));
        setSolicitacao(parsed);
      } catch (e) {
        console.error('Erro ao parsear dados:', e);
      }
    }
    
    registrarAbastecimento();
  }, []);

  // Atualizar progresso baseado na etapa
  useEffect(() => {
    const newProgress = (etapaAtual / etapas.length) * 100;
    setProgress(newProgress);
    
    // Atualizar status das etapas
    etapas.forEach((etapa, index) => {
      if (index < etapaAtual) {
        etapa.status = 'completed';
      } else if (index === etapaAtual) {
        etapa.status = 'loading';
      } else {
        etapa.status = 'pending';
      }
    });
  }, [etapaAtual]);

  async function registrarAbastecimento() {
    try {
      // Etapa 1: Validando dados
      setEtapaAtual(0);
      setStatus('Validando dados do abastecimento...');
      await delay(800);
      
      if (!solicitacaoId || !litros) {
        throw new Error('Dados do abastecimento incompletos');
      }

      // Etapa 2: Obtendo localização
      setEtapaAtual(1);
      setStatus('Obtendo localização...');
      
      let geolocal = '';
      try {
        const location = await getGeolocation();
        geolocal = location;
        console.log('Localização obtida:', geolocal);
      } catch (e) {
        console.log('Erro ao obter localização:', e);
        // Continua mesmo sem localização
      }
      
      await delay(500);

      // Etapa 3: Enviando para o servidor
      setEtapaAtual(2);
      setStatus('Enviando dados para o servidor...');

      const payload = {
        id_abastecimento: Number(solicitacaoId),
        litros: Number(litros),
        geolocal: geolocal || undefined,
      };
      
      console.log('[DEBUG] Payload para API:', payload);
      
      const response = await apiService.registrarAbastecimento(payload);
      
      if (!response.sucesso) {
        throw new Error(response.mensagem || 'Erro ao registrar abastecimento');
      }

      // Etapa 4: Confirmando registro
      setEtapaAtual(3);
      setStatus('Confirmando registro no servidor...');
      await delay(500);

      // Sucesso!
      setStatus('Abastecimento registrado com sucesso!');
      setProgress(100);
      
      // Marcar todas etapas como completas
      setEtapaAtual(etapas.length);
      
      const agora = new Date();
      const dataHora = agora.toLocaleString('pt-BR', {
        day: '2-digit', month: '2-digit', year: 'numeric',
        hour: '2-digit', minute: '2-digit',
      });

      // Aguardar e redirecionar
      setTimeout(() => {
        navigate(`/conclusao?litros=${litros}&dados=${encodeURIComponent(dados)}&dataHora=${encodeURIComponent(dataHora)}`);
      }, 1500);
      
    } catch (error) {
      console.error('Erro ao registrar abastecimento:', error);
      setStatus('Erro ao processar abastecimento');
      setErro(error.message || 'Erro de conexão com o servidor');
      setProgress(0);
    }
  }

  function delay(ms) {
    return new Promise(resolve => setTimeout(resolve, ms));
  }

  return (
    <div className="processando-web">
      <div className="processando-card">
        {/* Ícone de status */}
        <div className={`status-icon-wrapper ${!erro && progress === 100 ? 'success' : ''}`}>
          {erro ? (
            <FaExclamationTriangle className="status-icon error" />
          ) : progress === 100 ? (
            <FaCheckCircle className="status-icon success" />
          ) : (
            <FaSpinner className="status-icon loading spin" />
          )}
        </div>

        {/* Título e status */}
        <h2 className="processando-title">
          {erro ? 'Erro no Processamento' : 'Processando Abastecimento'}
        </h2>
        <p className="processando-status">{status}</p>

        {/* Barra de progresso */}
        <div className="progress-web-container">
          <div className="progress-web-bar" style={{ width: `${progress}%` }}>
            <div className="progress-glow-web"></div>
          </div>
        </div>
        <div className="progress-percentage-web">{Math.round(progress)}%</div>

        {/* Lista de etapas */}
        <div className="etapas-web-container">
          {etapas.map((etapa, index) => (
            <div 
              key={etapa.id} 
              className={`etapa-web-item ${etapa.status}`}
            >
              <div className="etapa-web-icon">
                {etapa.status === 'completed' ? (
                  <FaCheckCircle className="icon-completed" />
                ) : etapa.status === 'loading' ? (
                  <FaSpinner className="icon-loading spin" />
                ) : (
                  etapa.icon
                )}
              </div>
              <div className="etapa-web-content">
                <span className="etapa-web-nome">{etapa.nome}</span>
                {etapa.status === 'completed' && (
                  <span className="etapa-web-status">Concluído</span>
                )}
                {etapa.status === 'loading' && (
                  <span className="etapa-web-status in-progress">Processando</span>
                )}
              </div>
            </div>
          ))}
        </div>

        {/* Informações do abastecimento */}
        {solicitacao && !erro && progress < 100 && (
          <div className="info-abastecimento-web">
            <h4>Informações do Abastecimento</h4>
            <div className="info-row-web">
              <span>Placa:</span>
              <strong>{solicitacao.veiculo?.placa || '-'}</strong>
            </div>
            <div className="info-row-web">
              <span>Litros:</span>
              <strong>{litros} L</strong>
            </div>
            <div className="info-row-web">
              <span>Posto:</span>
              <strong>{solicitacao.posto_nome || 'Posto validado'}</strong>
            </div>
          </div>
        )}

        {/* Mensagem de erro */}
        {erro && (
          <div className="erro-web-container">
            <FaExclamationTriangle className="erro-web-icon" />
            <div className="erro-web-content">
              <h4>Ocorreu um erro</h4>
              <p>{erro}</p>
              <button 
                className="retry-web-btn"
                onClick={() => window.location.reload()}
              >
                Tentar novamente
              </button>
            </div>
          </div>
        )}

        {!erro && progress < 100 && (
          <div className="dica-web-container">
            <FaLightbulb className="dica-web-icon" />
            <div className="dica-web-content">
              <strong>Não feche esta janela</strong>
              <p>O processo está em andamento e será concluído em alguns segundos.</p>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}